import { Link } from 'react-router-dom';

const TermsOfService = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Hero Banner */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-sky-900 text-white">
        <div className="mx-auto px-4 py-12 sm:px-6 lg:px-8">
          <Link to="/register" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white mb-6 transition">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Registration
          </Link>
          <div>
            <div className="inline-block mb-4 px-4 py-2 rounded-full bg-sky-500/20 border border-sky-400/30 backdrop-blur-sm">
              <p className="text-xs font-semibold uppercase tracking-widest text-sky-200">Transparency & Trust</p>
            </div>
            <h1 className="text-5xl font-extrabold tracking-tight mb-4">Terms of Service</h1>
            <p className="text-lg text-slate-300">
              Understanding our terms helps us build a trustworthy platform for businesses and consumers alike. Your rights and responsibilities matter to us.
            </p>
            <p className="mt-6 text-sm text-slate-400">Last updated: March 16, 2026</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-5">
          {/* Sidebar Navigation */}
          <aside className="lg:col-span-1">
            <div className="sticky top-8 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-slate-600 mb-4">Sections</h3>
              <nav className="space-y-2.5">
                {[
                  { id: 'acceptance', label: '1. Acceptance of Terms' },
                  { id: 'use', label: '2. Use License' },
                  { id: 'disclaimer', label: '3. Disclaimer' },
                  { id: 'limitations', label: '4. Limitations' },
                  { id: 'accuracy', label: '5. Accuracy' },
                  { id: 'materials', label: '6. Materials' },
                  { id: 'links', label: '7. Links' },
                  { id: 'modifications', label: '8. Modifications' },
                  { id: 'contact', label: '9. Contact' },
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="block text-sm text-slate-600 hover:text-sky-600 transition-colors text-left font-medium py-1"
                  >
                    {item.label}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <div className="lg:col-span-4 space-y-10">
            {/* Section 1 */}
            <section id="acceptance" className="scroll-mt-8 bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">1. Acceptance of Terms</h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  By accessing and using the VMC Reviews platform ("Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this Service.
                </p>
                <p>
                  VMC Reviews reserves the right to modify these terms and conditions at any time. Your continued use of the Service following the posting of revised terms and conditions will mean that you accept and agree to the changes. Each time you use our Service, the current version of these Terms of Service will apply.
                </p>
              </div>
            </section>

            {/* Section 2 */}
            <section id="use" className="scroll-mt-8 bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">2. Use License</h2>
              <div className="space-y-6">
                <p className="text-slate-700 leading-relaxed">
                  Permission is granted to temporarily download one copy of the materials (information or software) on VMC Reviews for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
                </p>
                <ul className="space-y-3 ml-4">
                  <li className="text-slate-700 leading-relaxed flex gap-3">
                    <span className="text-sky-600 font-bold shrink-0">•</span>
                    <span>Modify or copy the materials</span>
                  </li>
                  <li className="text-slate-700 leading-relaxed flex gap-3">
                    <span className="text-sky-600 font-bold shrink-0">•</span>
                    <span>Use the materials for any commercial purpose or for any public display</span>
                  </li>
                  <li className="text-slate-700 leading-relaxed flex gap-3">
                    <span className="text-sky-600 font-bold shrink-0">•</span>
                    <span>Attempt to decompile or reverse engineer any software contained on VMC Reviews</span>
                  </li>
                  <li className="text-slate-700 leading-relaxed flex gap-3">
                    <span className="text-sky-600 font-bold shrink-0">•</span>
                    <span>Remove any copyright or other proprietary notations from the materials</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 3 */}
            <section id="disclaimer" className="scroll-mt-8 bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">3. Disclaimer</h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  The materials on VMC Reviews are provided "as is". VMC Reviews makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.
                </p>
                <p>
                  This disclaimer does not apply to the extent prohibited by applicable law. VMC Reviews does not warrant that the materials on VMC Reviews or any third-party information provided through the Service is accurate, complete, or current.
                </p>
              </div>
            </section>

            {/* Section 4 */}
            <section id="limitations" className="scroll-mt-8 bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">4. Limitations</h2>
              <div className="space-y-4">
                <p className="text-slate-700 leading-relaxed">
                  In no event shall VMC Reviews or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on VMC Reviews, even if VMC Reviews or an authorized representative has been notified orally or in writing of the possibility of such damage.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4">
                  <p className="text-sm text-amber-900">
                    <strong>⚠️ Important:</strong> Because some jurisdictions do not allow limitations on implied warranties, or limitations of liability for consequential or incidental damages, these limitations may not apply to you.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 5 */}
            <section id="accuracy" className="scroll-mt-8 bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">5. Accuracy of Materials</h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  The materials appearing on VMC Reviews could include technical, typographical, or photographic errors. VMC Reviews does not warrant that any of the materials on VMC Reviews are accurate, complete, or current. VMC Reviews may make changes to the materials contained on VMC Reviews at any time without notice.
                </p>
                <p>
                  Users are responsible for verifying information they provide and any information they rely upon. VMC Reviews shall not be liable for any reliance on, or inaccuracy of, information provided by third parties through the Service.
                </p>
              </div>
            </section>

            {/* Section 6 */}
            <section id="materials" className="scroll-mt-8 bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">6. User-Generated Content and Materials</h2>
              <div className="space-y-6">
                <p className="text-slate-700 leading-relaxed">
                  By submitting reviews, ratings, or other content to VMC Reviews, you grant VMC Reviews a non-exclusive, worldwide, royalty-free license to use, modify, reproduce, and distribute such content. You represent and warrant that you own or have the necessary rights to the content you submit.
                </p>
                <div>
                  <p className="text-slate-700 font-medium mb-3">You agree that your content will not contain:</p>
                  <ul className="space-y-2 ml-4">
                    <li className="text-slate-700 flex gap-3">
                      <span className="text-sky-600 font-bold shrink-0">•</span>
                      <span>Defamatory, abusive, or hateful language</span>
                    </li>
                    <li className="text-slate-700 flex gap-3">
                      <span className="text-sky-600 font-bold shrink-0">•</span>
                      <span>Spam or advertising</span>
                    </li>
                    <li className="text-slate-700 flex gap-3">
                      <span className="text-sky-600 font-bold shrink-0">•</span>
                      <span>Content that infringes intellectual property rights</span>
                    </li>
                    <li className="text-slate-700 flex gap-3">
                      <span className="text-sky-600 font-bold shrink-0">•</span>
                      <span>False or misleading information</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 7 */}
            <section id="links" className="scroll-mt-8 bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">7. Links</h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  VMC Reviews has not reviewed all of the sites linked to its website and is not responsible for the contents of any such linked site. The inclusion of any link does not imply endorsement by VMC Reviews of the site. Use of any such linked website is at the user's own risk.
                </p>
                <p>
                  We reserve the right to terminate a link agreement at any time. If you are going to link to our website, you may only use a text link with the words "VMC Reviews Business Platform" without our prior approval.
                </p>
              </div>
            </section>

            {/* Section 8 */}
            <section id="modifications" className="scroll-mt-8 bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">8. Modifications</h2>
              <p className="text-slate-700 leading-relaxed">
                VMC Reviews may revise these terms of service for the website at any time without notice. By using this website, you are agreeing to be bound by the then current version of these terms of service.
              </p>
            </section>

            {/* Section 9 */}
            <section id="contact" className="scroll-mt-8 bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">9. Contact Information</h2>
              <p className="text-slate-700 leading-relaxed mb-6">
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <div className="bg-slate-50 rounded-xl p-6 space-y-4 border border-slate-200">
                <div>
                  <p className="text-sm text-slate-600 font-medium mb-1">Email</p>
                  <a href="mailto:support@vmcreviews.com" className="text-sky-600 font-medium hover:text-sky-700">support@vmcreviews.com</a>
                </div>
                <div>
                  <p className="text-sm text-slate-600 font-medium mb-1">Address</p>
                  <p className="text-slate-900">VMC Reviews Platform<br />India</p>
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <div className="mt-10 pt-10 border-t-2 border-slate-200">
              <div className="rounded-2xl bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50 p-8 border border-sky-200 shadow-sm">
                <div className="flex items-start justify-between gap-6">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">Ready to join our community?</h3>
                    <p className="text-slate-700 leading-relaxed">Start sharing verified experiences and discover trusted local businesses around you.</p>
                  </div>
                  <Link
                    to="/register"
                    className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-sky-600 to-blue-600 px-8 py-3 text-sm font-bold text-white hover:shadow-lg hover:shadow-sky-200 transition-all duration-200 hover:-translate-y-0.5"
                  >
                    Create Account
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
